"use client";

import { Loader2 } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { MobileSidebar, MobileTopBar, Sidebar } from "@/components/layout/sidebar";
import { SuperAdminGuard } from "@/components/auth/super-admin-guard";
import { PUBLIC_PATHS } from "@/lib/constants";
import { getSupabase } from "@/lib/supabase/client";

type AuthState = "loading" | "authed" | "anon";

/**
 * CMS shell guard.
 *
 * 1. Public path'lerse children'ı kapsamadan render eder (login, forgot, reset).
 * 2. Oturum yoksa `/login?redirect=...`'a yönlendirir.
 * 3. Oturum varsa app shell'i (sidebar + content area) sarar ve içeride
 *    `SuperAdminGuard` ile super-admin olmayan kullanıcıyı bloklar — CMS,
 *    sadece is_super_admin=true kullanıcı için açıktır.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const [state, setState] = useState<AuthState>("loading");

  const isPublic = PUBLIC_PATHS.has(pathname);

  useEffect(() => {
    let mounted = true;
    let supabase;
    try {
      supabase = getSupabase();
    } catch {
      if (mounted) setState("anon");
      return;
    }

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setState(session?.user ? "authed" : "anon");
    });

    let listener: { subscription?: { unsubscribe?: () => void } } | undefined;
    try {
      const { data } = supabase.auth.onAuthStateChange((_evt, session) => {
        if (!mounted) return;
        setState(session?.user ? "authed" : "anon");
      });
      listener = data;
    } catch {
      /* defensive */
    }
    return () => {
      mounted = false;
      try {
        listener?.subscription?.unsubscribe?.();
      } catch {
        /* ignore */
      }
    };
  }, []);

  useEffect(() => {
    if (isPublic) return;
    if (state === "anon") {
      const params = new URLSearchParams();
      if (pathname !== "/") params.set("redirect", pathname);
      router.replace(`/login${params.size ? `?${params.toString()}` : ""}`);
    }
  }, [state, isPublic, pathname, router]);

  if (isPublic) return <>{children}</>;

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-5 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (state === "anon") {
    return null;
  }

  return (
    <SuperAdminGuard>
      <AppShell>{children}</AppShell>
    </SuperAdminGuard>
  );
}

// F13 cluster J — mobile drawer state lives at shell scope.
function AppShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />
      <main className="flex-1 overflow-x-hidden bg-bg-page">
        <MobileTopBar onMenuClick={() => setMobileNavOpen(true)} />
        {children}
      </main>
    </div>
  );
}
